import React from 'react'

import clsx from 'clsx'

import BuildConfig from '$extensionSrc/buildConfig'
import { ScoreStatus } from '$extensionSrc/components/ImpactMetrics/types'
import LogContextProvider from '$extensionSrc/components/LogContextProvider'
import { extensionRedirectPath } from '$extensionSrc/consts/urlConsts'
import { PLATFORM } from '$extensionSrc/utils/enums'

import AccountSummary from './AccountSummary'
import KeyScoreSummary from './KeyScoreSummary'
import NoReport from './NoReport'
import OverallSummary from './OverallSummary'
import styles from './Page.module.scss'
import { ChecklistSummary, MetricsCategory } from './types'

export default function Page({
  overallScore,
  overallStatus,
  keyScoreStatus,
  checklistSummaries,
  highlights,
  todos,
  platformsWithAccounts,
  platformsWithoutAccounts,
  numAccounts,
  allAutoscansOn,
  timeSavedStr,
  lastUsedOnStr,
}: {
  overallScore: number | null
  overallStatus: ScoreStatus | null
  keyScoreStatus: Record<MetricsCategory, ScoreStatus | null>
  checklistSummaries: Record<MetricsCategory, ChecklistSummary>
  highlights: Record<MetricsCategory, string[]>
  todos: Record<MetricsCategory, string[]>
  platformsWithAccounts: PLATFORM[]
  platformsWithoutAccounts: PLATFORM[]
  numAccounts: number
  allAutoscansOn: boolean
  timeSavedStr: string | null
  lastUsedOnStr: string | null
}) {
  const lastUsedOn = lastUsedOnStr ? new Date(lastUsedOnStr) : null

  let summary
  switch (overallStatus) {
    case 'Minimal':
      summary =
        "You've taken the first steps to protect your online presence. Your full report below goes over some quick wins to boost your protection."
      break
    case 'Moderate':
      summary =
        "You're making great progress! See your full report below for details on what you've locked down and simple ways to maximize your protection."
      break
    case 'High':
      summary = `You're getting the most out of ${BuildConfig.TENANT_NAME}! See your full report below for a deeper look at what you've locked down.`
      break
    default:
      summary =
        "You've taken the first steps to protect your online presence. Your full report below goes over some quick wins to boost your protection."
  }

  const ctaTextMap: Record<MetricsCategory, string> = {
    'Data Ownership':
      keyScoreStatus?.['Data Ownership'] === 'High'
        ? 'See to-dos'
        : 'Finish to-dos',
    'Digital Wellbeing':
      keyScoreStatus?.['Digital Wellbeing'] === 'High'
        ? 'See to-dos'
        : 'Finish to-dos',
    'Online Safety':
      keyScoreStatus?.['Online Safety'] === 'High'
        ? 'See to-dos'
        : 'Finish to-dos',
  }

  const baseUrl = new URL(extensionRedirectPath, window.location.href)

  const addHash = (hash: string) => {
    const url = new URL(baseUrl)
    url.hash = hash
    return url
  }
  const urlAddAccounts = addHash('add-accounts')
  const urlManageAccounts = addHash('manage-accounts')
  const urlRemovePlatforms = addHash('remove-platforms')

  const onClickCta = () => {
    window.location.assign(baseUrl.href)
  }

  const showReport = overallScore !== null && overallScore !== undefined

  const logEventProps = {
    component: 'ImpactMetricsReportPage',
    overallScore,
    overallStatus,
  }

  return (
    <LogContextProvider {...logEventProps}>
      <div className={clsx('Page', !showReport && styles.noReport)}>
        <div className={styles.container}>
          {overallScore !== null && overallScore !== undefined ? (
            <>
              <OverallSummary
                lastUsedOn={lastUsedOn}
                score={overallScore}
                status={overallStatus}
                summary={summary}
              />
              <AccountSummary
                allAutoscansOn={allAutoscansOn}
                numAccounts={numAccounts}
                platformsWithAccounts={platformsWithAccounts}
                platformsWithoutAccounts={platformsWithoutAccounts}
                timeSavedStr={timeSavedStr}
                urlAddAccounts={urlAddAccounts.href}
                urlManageAccounts={urlManageAccounts.href}
                urlRemovePlatforms={urlRemovePlatforms.href}
              />
              {['Online Safety', 'Digital Wellbeing', 'Data Ownership'].map(
                (category) =>
                  keyScoreStatus[category] && (
                    <KeyScoreSummary
                      category={category}
                      checklistSummary={checklistSummaries[category]}
                      ctaText={ctaTextMap[category]}
                      highlights={highlights[category]}
                      key={category}
                      onClickCta={onClickCta}
                      status={keyScoreStatus[category]}
                      todos={todos[category]}
                    />
                  ),
              )}
            </>
          ) : (
            <NoReport onClickCta={onClickCta} />
          )}
        </div>
      </div>
    </LogContextProvider>
  )
}
